import { useMutation } from '@apollo/client';
import { produce } from 'immer';
import React from 'react';
import { Link } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import { SimpleTooltip } from '@eluve/components';
import { Button, Icon, Stack, useToast } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useIsEluveAdmin, useUserIdFromSession } from '@eluve/session-helpers';

import { useAddUserToTenant } from '../$tenantId/users/hooks/useAddUserToTenant';

import { DeleteTenantAction } from './DeleteTenantAction';
import { RenameTenantAction } from './RenameTenantAction';
import { getAdminTenantsSummaryFilterQuery } from './query';

const removeUserFromTenantMutation = graphql(`
  mutation removeUserFromTenant($tenantId: uuid!, $userId: uuid!) {
    deleteTenantUsersByPk(tenantId: $tenantId, userId: $userId) {
      tenantId
      userId
    }
  }
`);

export const usersTenantsFragment = graphql(`
  fragment UsersTenants on Users {
    __typename
    id
    tenants {
      __typename
      userId
      tenantId
      role
    }
  }
`);

export interface TenantActionsColumnProps {
  tenantId: string;
}

export const TenantActionsColumn: React.FC<TenantActionsColumnProps> = ({
  tenantId,
}) => {
  const userId = useUserIdFromSession();
  const toast = useToast();
  const apiClient = useApiClient();
  const isEluveAdmin = useIsEluveAdmin();
  const addUserToTenant = useAddUserToTenant(tenantId, async () => {
    cacheUtils.updateFragment(
      {
        fragment: usersTenantsFragment,
        key: { id: userId },
      },
      (existing) => {
        if (!existing) return existing;

        return produce(existing, (draft) => {
          draft.tenants.push({
            tenantId,
            __typename: 'TenantUsers' as const,
            userId,
            role: 'tenant-admin',
          });
        });
      },
    );

    await apiClient.auth.createTenantSession({
      body: {
        tenantId,
      },
    });
  });

  const [removeUser] = useMutation(removeUserFromTenantMutation, {
    refetchQueries: [getAdminTenantsSummaryFilterQuery],
    onCompleted: (data) => {
      toast.success('User removed from organization');
      // When an eluve admin removes themselves from a tenant, also update their session
      // to remove the tenant context from their JWT immediatley instead of waiting
      // for next login
      if (data?.deleteTenantUsersByPk?.userId === userId) {
        apiClient.auth.createTenantSession();
      }
    },
    onError: () => toast.error('Error removing user from organization'),
    update: () => {
      cacheUtils.updateFragment(
        {
          fragment: usersTenantsFragment,
          key: { id: userId },
        },
        (existing) => {
          if (!existing) return existing;

          return {
            ...existing,
            tenants: existing.tenants.filter((t) => t.tenantId !== tenantId),
          };
        },
      );
    },
  });

  const usersTenants = useCompleteFragment({
    fragment: usersTenantsFragment,
    key: { id: userId },
    strict: false,
  });

  const isProvisioned = (usersTenants?.tenants ?? []).some(
    (t) => t.tenantId === tenantId,
  );

  const isAdminOfTenant = (usersTenants?.tenants ?? []).some(
    (t) => t.tenantId === tenantId && t.role === 'tenant-admin',
  );

  const currentTenantCount = usersTenants?.tenants?.length ?? 0;
  const maxTenantLimit = 20;
  const hasReachedTenantLimit = currentTenantCount >= maxTenantLimit;

  const handleProvision = () => {
    if (hasReachedTenantLimit) {
      toast.error('You have hit maximum tenants provisioned');
      return;
    }

    // Provisioned admins are transient internal accounts — exclude them from
    // usage reporting at insert, same as the command-palette provision flow.
    addUserToTenant({
      variables: {
        tenantId,
        userId,
        role: 'tenant-admin',
        isExcluded: true,
      },
    });
  };

  return (
    <Stack direction="horizontal" fullWidth={false} gap={2}>
      {isProvisioned && (
        <>
          <Button
            onClick={() =>
              removeUser({
                variables: {
                  tenantId,
                  userId,
                },
              })
            }
            size="xs"
            variant="destructive"
          >
            Deprovision
          </Button>
          <Link to={`${tenantId}/dash`}>
            <Button variant="outline" aria-label="Dashboard">
              <SimpleTooltip label="Dashboard">
                <Icon name="LayoutDashboard" size="xl" />
              </SimpleTooltip>
            </Button>
          </Link>
          <Link to={`${tenantId}/users`}>
            <Button variant="outline" aria-label="Users">
              <SimpleTooltip label="Users">
                <Icon name="Users" size="xl" />
              </SimpleTooltip>
            </Button>
          </Link>
          {isAdminOfTenant && <RenameTenantAction tenantId={tenantId} />}
        </>
      )}
      {!isProvisioned && (
        <Button size="xs" onClick={handleProvision}>
          Provision
        </Button>
      )}
      {isEluveAdmin && <DeleteTenantAction tenantId={tenantId} />}
    </Stack>
  );
};
